import { AppNode } from '../types';
import type { Edge } from '../types';
import { ExpressionEngine } from '../utils/expressionEngine';

export interface ExecutionResult {
  nextNodes: string[]; // Changed from single ID to array for parallel support
  logs: string[];
  traversedEdgeIds: string[]; // Track which edges were used
}

/**
 * Determines the next node(s) in the workflow based on the current node type, edges, and action data.
 */
export const determineNextNode = (
  node: AppNode, 
  edges: Edge[], 
  actionData: any, 
  variables: any
): ExecutionResult => {
  const outgoingEdges = edges.filter(e => e.source === node.id);
  
  // Check for explicit boundary trigger (takes precedence over standard flow)
  if (actionData && actionData.triggerBoundaryId) {
      const boundaryEdge = edges.find(e => 
          e.source === node.id && 
          e.sourceHandle === 'boundary' && 
          e.target === actionData.triggerBoundaryId
      );
      
      if (boundaryEdge) {
          return { 
              nextNodes: [boundaryEdge.target], 
              logs: [`Boundary event triggered: ${actionData.boundaryLabel || 'Interruption'}`],
              traversedEdgeIds: [boundaryEdge.id] 
          };
      }
  }

  if (outgoingEdges.length === 0) {
      return { nextNodes: [], logs: ['End of flow reached.'], traversedEdgeIds: [] };
  }

  // Strategy Pattern based on Node Type
  switch(node.type) {
    case 'XorGateway':
      return handleXorGateway(node, outgoingEdges, actionData);
    case 'InclusiveGateway':
      return handleInclusiveGateway(node, outgoingEdges, actionData);
    case 'ParallelGateway':
      return handleParallelGateway(node, outgoingEdges, actionData);
    case 'UserTask':
      return handleUserTask(node, outgoingEdges, actionData);
    default:
      // Default: Follow first edge (Sequence Flow)
      // Exclude boundary edges for standard "Next" clicks
      const standardEdges = outgoingEdges.filter(e => e.sourceHandle !== 'boundary');
      if (standardEdges.length > 0) {
          return { 
              nextNodes: [standardEdges[0].target], 
              logs: [], 
              traversedEdgeIds: [standardEdges[0].id] 
          };
      }
      return { nextNodes: [], logs: ['No standard outgoing path found.'], traversedEdgeIds: [] };
  }
};

/**
 * Pre-evaluates node logic (e.g. gateway conditions) when entering a node.
 * Returns suggested data (e.g. path index).
 */
export const evaluateNode = (node: AppNode, variables: any): string | null => {
     if (node.type === 'XorGateway') {
          const conditions = node.data.conditions || [];
          let matchIndex = -1;
          
          for (const cond of conditions) {
              if (cond.expression) {
                  if (ExpressionEngine.evaluateCondition(cond.expression, variables)) {
                      matchIndex = cond.path;
                      break;
                  }
              } else if (matchIndex === -1) {
                   // Capture default path (empty expression) if seen, but keep looking for explicit matches
                   matchIndex = cond.path; 
              }
          }
          // Fallback to last path if defined and nothing matched
          if (matchIndex === -1 && conditions.length > 0) {
              matchIndex = conditions[conditions.length - 1].path;
          }
          
          return String(matchIndex);
     }
     return null;
}

// --- Strategy Implementations ---

const handleXorGateway = (node: AppNode, edges: Edge[], actionData: any): ExecutionResult => {
  const selectedPathIndex = actionData?.selectedPath;
  const edge = edges.find(e => e.sourceHandle === String(selectedPathIndex));
  
  if (edge) {
    return { 
        nextNodes: [edge.target], 
        logs: [`Gateway decision: Path ${selectedPathIndex}`],
        traversedEdgeIds: [edge.id]
    };
  }
  return { nextNodes: [], logs: [`Error: Selected path ${selectedPathIndex} not found.`], traversedEdgeIds: [] };
};

const handleInclusiveGateway = (node: AppNode, edges: Edge[], actionData: any): ExecutionResult => {
    // In this simulator, we treat Inclusive similar to Xor for simplicity in the UI, 
    // unless the user selects multiple (not currently implemented in UI).
    const selectedPathIndex = actionData?.selectedPath;
    
    if (selectedPathIndex !== undefined) {
         let edge = edges.find(e => e.sourceHandle === String(selectedPathIndex));
         if (!edge && edges[selectedPathIndex]) {
             edge = edges[selectedPathIndex];
         }

         if (edge) return { 
             nextNodes: [edge.target], 
             logs: [`Inclusive Gateway: Continuing on path ${selectedPathIndex}`],
             traversedEdgeIds: [edge.id]
         };
    }

    if (edges.length > 0) {
        return { 
            nextNodes: [edges[0].target], 
            logs: ['Inclusive Gateway: Defaulting to first path'],
            traversedEdgeIds: [edges[0].id]
        };
    }
    return { nextNodes: [], logs: ['No path found.'], traversedEdgeIds: [] };
};

const handleParallelGateway = (node: AppNode, edges: Edge[], actionData: any): ExecutionResult => {
    // If it's a merging gateway, we just continue to the next single output
    if (node.data.isMerging) {
        if (edges.length > 0) {
             return { 
                nextNodes: [edges[0].target], 
                logs: ['Parallel Merge: Proceeding.'],
                traversedEdgeIds: [edges[0].id]
            };
        }
        return { nextNodes: [], logs: ['Parallel Merge: No outgoing path.'], traversedEdgeIds: [] };
    }

    // Split: Activate ALL outgoing edges (excluding boundary associations which shouldn't be on gateways anyway)
    const standardEdges = edges.filter(e => e.sourceHandle !== 'boundary');
    const targets = standardEdges.map(e => e.target);
    const edgeIds = standardEdges.map(e => e.id);
    
    return { 
        nextNodes: targets, 
        logs: [`Parallel Split: Activating ${targets.length} paths.`],
        traversedEdgeIds: edgeIds
    };
}

const handleUserTask = (node: AppNode, edges: Edge[], actionData: any): ExecutionResult => {
    // Standard flow
    const standardEdge = edges.find(e => !e.sourceHandle || e.sourceHandle === 'default');
    const nextEdge = standardEdge || edges.find(e => e.sourceHandle !== 'boundary') || edges[0];
    
    if (nextEdge) {
        return { 
            nextNodes: [nextEdge.target], 
            logs: ['User task completed.'],
            traversedEdgeIds: [nextEdge.id]
        };
    }
    return { nextNodes: [], logs: ['No outgoing path.'], traversedEdgeIds: [] };
};